import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './Setdate.css'; // 引入CSS檔案

const RefereeInterface = () => {
  const teamsContainerRef = useRef(null);
  const [schedules, setSchedules] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState('');
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState('');
  const [refereeName, setRefereeName] = useState('');
  const [refereeContact, setRefereeContact] = useState('');
  const [updatedScores, setUpdatedScores] = useState({});
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [reverseTeams, setReverseTeams] = useState(false);
  const [teamBackgroundColors, setTeamBackgroundColors] = useState(['blue', 'red']); // 初始背景色
  const [touchStartY, setTouchStartY] = useState(null);

  useEffect(() => {
    fetchSchedules(); // 调用获取赛程的函数
  }, []);

  const fetchSchedules = () => {
    axios.get('/api/schedules')
      .then(response => {
        if (response.data && response.data.length > 0) {
          const inProgressSchedules = response.data.filter(schedule => schedule.status === '進行中');
          setSchedules(inProgressSchedules);
        } else {
          console.error('No schedules found.');
          setSchedules([]); // 确保状态被正确初始化，即使没有数据
        }
      })
      .catch(error => console.error('Error fetching schedules:', error));
  };

  const handleCourtChange = (e) => {
    const court = e.target.value;
    setSelectedCourt(court);
    const filtered = schedules.filter(schedule => schedule.court.toString() === court);
    setFilteredSchedules(filtered);
    if (filtered.length > 0) {
      setSelectedScheduleId(filtered[0]._id);
    } else {
      setSelectedScheduleId('');
    }
  };

  const handleRefereeChange = (e) => {
    const name = e.target.value;
    setRefereeName(name);
  };

  const handleRefereeContactChange = (e) => {
    const contact = e.target.value;
    setRefereeContact(contact);
  };

  // 通用通知函式，接收任意訊息
  const handleNotify = async (message) => {
    if (!selectedCourt) {
      alert('請先選擇場地！');
      return;
    }
    try {
      // 傳送含 message 的支援請求
      await axios.post(`/api/support/${selectedCourt}`, { message });
      alert(`${message} 已發送`);
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('通知發送失敗');
    }
  };

  // 請求支援 API 呼叫
  const handleSupportRequest = () => {
    if (!selectedCourt) {
      alert('請先選擇場地，才能請求支援！');
      return;
    }
    handleNotify(`第 ${selectedCourt} 場地請求支援`);
  };

  const handleScheduleChange = (e) => {
    setSelectedScheduleId(e.target.value);
  };

  const handleTouchStart = (e) => {
    setTouchStartY(e.touches[0].clientY);
  };

  const handleTouchEnd = (teamIndex, e) => {
    if (touchStartY !== null) {
      const touchEndY = e.changedTouches[0].clientY;
      const deltaY = touchEndY - touchStartY;

      if (deltaY < -30) { // 向上拨动增加分数
        handleScoreChange(teamIndex, 1);
      } else if (deltaY > 30) { // 向下拨动减少分数
        handleScoreChange(teamIndex, -1);
      }

      setTouchStartY(null);
    }
  };

  const handleScoreChange = (teamIndex, delta) => {
    const currentSchedule = schedules.find(schedule => schedule._id === selectedScheduleId);
    if (!currentSchedule) return;
    // 根據是否反轉隊伍決定實際索引
    const actualIndex = reverseTeams
      ? currentSchedule.teams.length - 1 - teamIndex
      : teamIndex;
    // 先計算新的分數
    const existingScore = updatedScores[selectedScheduleId]?.[actualIndex] ?? currentSchedule.teams[actualIndex].score;
    const newScore = Math.max(0, existingScore + delta);
    // 計算並更新本地分數狀態
    const newScoresForSchedule = {
      ...(updatedScores[selectedScheduleId] || {}),
      [actualIndex]: newScore
    };
    setUpdatedScores(prevScores => ({
      ...prevScores,
      [selectedScheduleId]: newScoresForSchedule
    }));
    // 構造更新後的隊伍數據並傳送到後端
    const teamsForUpdate = currentSchedule.teams.map((team, idx) => {
      const score = newScoresForSchedule[idx] ?? team.score;
      return { ...team, score };
    });
    axios.put(`/api/schedules/${selectedScheduleId}`, { teams: teamsForUpdate })
      .then(() => {
        console.log('分數已更新');
        fetchSchedules(); // 重新取得最新資料
      })
      .catch(error => console.error('Error updating score:', error));
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  const toggleTeamOrder = () => {
    // 僅更新 UI 反轉狀態與背景色，不改動後端隊伍順序
    setReverseTeams(!reverseTeams);
    setTeamBackgroundColors(prevColors => [prevColors[1], prevColors[0]]);
  };

  const updateRefereeInfo = () => {
    if (selectedScheduleId) {
      axios.put(`/api/schedules/${selectedScheduleId}`, { referee: { name: refereeName, contact: refereeContact } })
        .then(() => {
          console.log('裁判信息已更新');
          alert('裁判信息已成功更新到数据库');
        })
        .catch(error => console.error('Error updating referee info:', error));
    } else {
      alert('请先选择一个赛程');
    }
  };

  const currentSchedule = schedules.find(schedule => schedule._id === selectedScheduleId);

  const uniqueCourts = [...new Set(schedules.map(schedule => schedule.court.toString()))];

  // 阻止下滑刷新動作
  const handleTouchMove = (e) => {
    e.preventDefault();
  };
  // 為 teams container 添加非被動 touchmove 監聽
  useEffect(() => {
    const el = teamsContainerRef.current;
    if (el) {
      el.addEventListener('touchmove', handleTouchMove, { passive: false });
    }
    return () => {
      if (el) {
        el.removeEventListener('touchmove', handleTouchMove);
      }
    };
  }, []);

  return (
    <div className={isFullScreen ? 'full-screen' : ''}>
      {isFullScreen && (
        <>
          <button onClick={toggleFullScreen} className="exit-fullscreen-button">
            退出全螢幕
          </button>
          <button onClick={toggleTeamOrder} className="swap-teams-button">
            ←　→
          </button>
          <button onClick={updateRefereeInfo} className="update-referee-button">
            更新裁判名稱
          </button>
          {/* 左側請求支援按鈕 */}
          <button onClick={handleSupportRequest} className="call-support-button" >
            請求支援
          </button>
        </>
      )}

      {currentSchedule && (
        <div className="schedule-item">
          <div className="teams-container" ref={teamsContainerRef}>
            {(reverseTeams ? [...currentSchedule.teams].reverse() : currentSchedule.teams).map((team, index) => {
              // 計算實際 teams 陣列索引
              const actualIndex = reverseTeams
                ? currentSchedule.teams.length - 1 - index
                : index;
              return (
                <div
                  key={index}
                  className="team-info"
                  style={{ backgroundColor: teamBackgroundColors[index] }} // 設定動態背景色
                  onTouchStart={handleTouchStart}
                  onTouchEnd={(e) => handleTouchEnd(index, e)}
                >
                  <div>
                    <p>隊伍：{team.teamName}</p>
                    <div>
                      {team.players.map((player, playerIndex) => (
                        <div key={playerIndex}>
                          {player.name}
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="score-display">
                    <span className="score">
                      {updatedScores[selectedScheduleId]?.[actualIndex] ?? currentSchedule.teams[actualIndex].score}
                    </span>
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!isFullScreen && (
        <div className="bottom-controls">
          <div className="control-row">
            <label>
              裁判姓名：
              <input
                type="text"
                value={refereeName}
                onChange={handleRefereeChange}
                style={{ fontSize: '16px' }}
              />
            </label>
            <label>
              聯絡方式：
              <input
                type="text"
                value={refereeContact}
                onChange={handleRefereeContactChange}
                style={{ fontSize: '16px' }}
              />
            </label>
          </div>
          <div className="control-row">
            <label>
              選擇場地：
              <select value={selectedCourt} onChange={handleCourtChange} style={{ fontSize: '16px' }}>
                <option value="">請選擇場地</option>
                {uniqueCourts.map(court => (
                  <option key={court} value={court}>
                    場地：{court}
                  </option>
                ))}
              </select>
            </label>
            {selectedCourt && (
              <label>
                選擇時間：
                <select value={selectedScheduleId} onChange={handleScheduleChange} style={{ fontSize: '16px' }}>
                  {filteredSchedules.map(schedule => (
                    <option key={schedule._id} value={schedule._id}>
                      {schedule.date} {schedule.time}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <div className="control-row">
            <button onClick={handleSupportRequest} className="call-button">
              請求支援
            </button>
          </div>
          {/* 生成每位參賽者的通知按鈕 */}
          {currentSchedule && (
            <div className="participant-notify">
              {currentSchedule.teams.flatMap(team =>
                team.players.map(player => (
                  <button
                    key={`${team.teamName}-${player.name}`}
                    className="call-button"
                    onClick={() => handleNotify(`呼叫 ${team.teamName} ${player.name}`)}
                  >
                    呼叫 {player.name}
                  </button>
                ))
              )}
            </div>
          )}
          <button onClick={toggleFullScreen} className="control-button">
            進入全螢幕
          </button>
        </div>
      )}

      {isFullScreen && (
        <>
          {/* ...full screen buttons... */}
          {/* 同樣在全螢幕下生成參賽者通知按鈕 */}
          {/* {currentSchedule && (
            <div className="participant-notify-fullscreen">
              {currentSchedule.teams.flatMap(team =>
                team.players.map(player => (
                  <button
                    key={`${team.teamName}-${player.name}`}
                    className="call-button"
                    onClick={() => handleNotify(`呼叫 ${team.teamName} ${player.name}`)}
                  >
                    呼叫 {team.teamName} {player.name}
                  </button>
                ))
              )}
            </div>
          )} */}
        </>
      )}
    </div>
  );
};

export default RefereeInterface;
